import { Injectable } from '@angular/core';
import {GroovePlanApiService} from "../grooveplan-api.service";
import {HttpClient} from "@angular/common/http";
import {StorageService} from "../internals/storage.service";
import {Router} from "@angular/router";
import {Subject, takeUntil} from "rxjs";
import {DroitsService} from "../internals/droits.service";
import { UserModel } from 'src/app/models/user.model';
import { SecureStorageService } from '../internals/secure-storage.service';
import { PreferencesService } from '../internals/preferences.service';

export type StoredUser = Partial<PublicUserStorage & SecureUserStorage>;

export interface PublicUserStorage {
  nom: string | undefined;
  prenom: string | undefined;
  photo?: string | null;
  regime: string | undefined;
  regime_id: number | undefined;
  createdAt: string | undefined;
  updatedAt: string | undefined;
}

export interface SecureUserStorage {
  id: number | undefined;
  email: string | undefined;
  telephone: string | undefined;
  admin: boolean | undefined;
  createdAt: string | undefined;
  updatedAt: string | undefined;
}

@Injectable({
  providedIn: 'root'
})
export class UserService extends GroovePlanApiService {
  constructor(_httpClient: HttpClient,
              storageService: StorageService,
              router: Router,
              secureStorage: SecureStorageService,
              private preferencesService: PreferencesService
            ) {
    super(_httpClient, storageService, secureStorage, router);
    this.entityUrl = 'user'
  }

  resetPassword(resetToken: string, newPassword: string) {
    return this.post('user/forgot-password/reset-password', {
      resetToken,
      newPassword
    });
  }

  // Previent le menu et le header quand la photo de profil de l'utilisateur courant change
  public currentUserPhoto$ = new Subject<string | null>();

  async uploadPhoto(photo: Blob, fileName: string) {
    const formData = new FormData();
    formData.append('photo', photo, fileName);
    const headers = await this.getAuthHeaders();
    return this.postFormData('user/photo', formData, headers);
  }

  async deletePhoto() {
    const headers = await this.getAuthHeaders();
    return this.delete('user', 'photo', headers);
  }

  // Met a jour la photo dans l'utilisateur stocke (persistance entre les lancements) et notifie l'app
  async setCurrentUserPhoto(photo: string | null) {
    const storedUser = await this.getUserInStorage();
    if (storedUser) {
      this.storeUserInStorage({ ...storedUser, photo });
    }
    this.currentUserPhoto$.next(photo);
  }

  // Supprime (anonymise) le compte de l'utilisateur connecte. Refuse par l'API en procuration.
  async deleteAccount() {
    const headers = await this.getAuthHeaders();
    return this.delete('user', 'me', headers);
  }

  // Apres suppression du compte : on efface tout ce qui reste sur l'appareil (session, utilisateur, identifiant memorise)
  async clearDeviceDataAfterAccountDeletion() {
    this.storeUserInStorage(null);
    // remove() echoue sur une cle absente (ex: identifiantLogin jamais memorise) : on ignore cle par cle
    const keys = ['authToken', 'expAuthToken', 'refreshToken', 'expRefreshToken', 'comptePartage', 'identifiantLogin'];
    await Promise.all(keys.map(key => this.secureStorage.removeItem(key).catch(() => {})));
  }

  async changePassword(oldPassword: string, newPassword: string) {
    const headers = await this.getAuthHeaders();
    return this.post('user/change-password', {
      oldPassword,
      newPassword
    }, headers);
  }
  
  sendMailCode(email: string) {
    return this.post('user/forgot-password/send-code', {
      email
    });
  }

  verifyCode(code: string) {
    return this.post('user/forgot-password/verify-code', {
      code
    });
  }

  
  sendVerifyMailCode(email: string) {
    return this.post('user/verifyEmailCode/send-code', {
      email
    });
  }

  getAccountStatus(email: string) {
    return this.post('user/account-status', {
      email
    });
  }

  verifyEmailCode(code: string) {
    return this.post('user/verifyEmailCode', {
      code
    });
  }

  storeUserInStorage(
    user: StoredUser | null,
    storageId: string = 'user'
  ): void {

    if (!user) {
      this.preferencesService.set<PublicUserStorage | null>(storageId, null);
      this.secureStorage.set<SecureUserStorage | null>(storageId, null);
      return;
    }

    const publicData: PublicUserStorage = {
      nom: user.nom,
      prenom: user.prenom,
      photo: user.photo ?? null,
      regime: user.regime,
      regime_id: user.regime_id,
      createdAt: user.createdAt,
      updatedAt: user.updatedAt,
    };

    const secureData: SecureUserStorage = {
      id: user.id,
      email: user.email,
      telephone: user.telephone,
      admin: user.admin,
      createdAt: user.createdAt,
      updatedAt: user.updatedAt,
    };

    this.preferencesService.set<PublicUserStorage>(storageId, publicData);
    this.secureStorage.set<SecureUserStorage>(storageId, secureData);
  }

  async getUserInStorage(
    storageId: string = 'user'
  ): Promise<StoredUser | null> {
    const [publicData, secureData] = await Promise.all([
      this.preferencesService.get<PublicUserStorage>(storageId),
      this.secureStorage.get<SecureUserStorage>(storageId)
    ]);
    
    if (!publicData && !secureData) {
      return null;
    }

    return { ...(publicData ?? {}), ...(secureData ?? {}) };
  }
  
  async resetCurrentUser(storageId: string = 'user'): Promise<void> {
    try {
      await Promise.all([
        this.preferencesService.remove(storageId),
        this.secureStorage.removeItem(storageId)
      ]);
    } catch (error) {
      console.error('Erreur lors du reset user', error);
    }
  }
}

